import React, { useState } from 'react';
import { Sparkles, ChevronRight, ChevronLeft, RotateCcw, Loader2, AlertCircle, Lock } from 'lucide-react';
import { aiSuggestionApiService, HabitSuggestion, DiscoverRequest, AiLimitExceededError } from '../../services/aiSuggestionApi';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { SubscriptionTier } from '../../types';

const LIFE_AREAS = [
  'Health',
  'Focus & Productivity',
  'Mindfulness',
  'Learning',
  'Relationships',
  'Sleep',
  'Creativity',
  'Finance',
];

const STRUGGLES = [
  'Staying consistent with healthy habits',
  'Managing stress and mental overwhelm',
  'Staying focused and avoiding distractions',
  'Finding time for personal growth',
  'Building better daily routines',
  'Getting enough quality sleep',
];

const DAILY_TIMES = ['Under 15 min', '15–30 min', '30–60 min', 'Flexible / no limit'];

interface DiscoverPageProps {
  onSuggestionSelect: (suggestion: HabitSuggestion, suggestions: HabitSuggestion[]) => void;
  onBack: () => void;
  initialSuggestions?: HabitSuggestion[];
}

export default function DiscoverPage({ onSuggestionSelect, onBack, initialSuggestions }: DiscoverPageProps) {
  const { getCurrentTier } = useSubscriptionStore();
  const [step, setStep] = useState(initialSuggestions && initialSuggestions.length > 0 ? 5 : 1);
  const [lifeAreas, setLifeAreas] = useState<string[]>([]);
  const [struggle, setStruggle] = useState('');
  const [dailyTime, setDailyTime] = useState('');
  const [goal, setGoal] = useState('');
  const [suggestions, setSuggestions] = useState<HabitSuggestion[]>(initialSuggestions ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tier = getCurrentTier();
  const isLocked = tier === SubscriptionTier.Free;

  const toggleLifeArea = (area: string) => {
    setLifeAreas(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    );
  };

  const canAdvance = () => {
    if (step === 1) return lifeAreas.length > 0;
    if (step === 2) return struggle !== '';
    if (step === 3) return dailyTime !== '';
    return true;
  };

  const handleNext = () => {
    if (step < 4) setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    const request: DiscoverRequest = {
      lifeAreas,
      struggle,
      dailyTime,
      goal: goal || undefined,
    };
    try {
      const results = await aiSuggestionApiService.discoverHabits(request);
      setSuggestions(results);
      setStep(5);
    } catch (err) {
      if (err instanceof AiLimitExceededError) {
        setError(
          `You've used all your AI suggestions for today (${err.usedToday}/${err.dailyLimit}). Try again tomorrow or upgrade your plan.`
        );
      } else {
        setError('Something went wrong generating your suggestions. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setLifeAreas([]);
    setStruggle('');
    setDailyTime('');
    setGoal('');
    setSuggestions([]);
    setError(null);
  };

  if (isLocked) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <PageHeader onBack={onBack} />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Upgrade to Discover Habits
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            AI-powered habit discovery is available on Basic and Pro plans. Answer a few questions and
            get 6 personalised habits built for your life.
          </p>
          <button
            onClick={onBack}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader onBack={onBack} />
      <div className="max-w-lg mx-auto px-4 py-8">
        {step <= 4 && (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                Find habits built for you
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Answer a few questions and we'll suggest habits that fit your life.
              </p>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-1">
              <div
                className="bg-indigo-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${(step / 4) * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">Step {step} of 4</p>

            {/* Step 1: Life areas */}
            {step === 1 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
                  What areas of life matter most to you?
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Select all that apply.</p>
                <div className="flex flex-wrap gap-2">
                  {LIFE_AREAS.map(area => (
                    <button
                      key={area}
                      onClick={() => toggleLifeArea(area)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        lifeAreas.includes(area)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Struggle */}
            {step === 2 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
                  What's your biggest struggle right now?
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Choose the one that resonates most.
                </p>
                <div className="flex flex-col gap-2">
                  {STRUGGLES.map(s => (
                    <button
                      key={s}
                      onClick={() => setStruggle(s)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-left transition-colors ${
                        struggle === s
                          ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-500 text-indigo-700 dark:text-indigo-300'
                          : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          struggle === s ? 'bg-indigo-600 border-indigo-600' : 'border-gray-400'
                        }`}
                      />
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Daily time */}
            {step === 3 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
                  How much time can you commit per day?
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Be realistic — small habits stick better.
                </p>
                <div className="flex flex-col gap-2">
                  {DAILY_TIMES.map(t => (
                    <button
                      key={t}
                      onClick={() => setDailyTime(t)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-left transition-colors ${
                        dailyTime === t
                          ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-500 text-indigo-700 dark:text-indigo-300'
                          : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          dailyTime === t ? 'bg-indigo-600 border-indigo-600' : 'border-gray-400'
                        }`}
                      />
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Goal */}
            {step === 4 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
                  What's your goal for the next 3 months?
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Optional but helps us personalise. E.g. "Lose 5kg and feel more energetic"
                </p>
                <textarea
                  value={goal}
                  onChange={e => setGoal(e.target.value)}
                  maxLength={150}
                  rows={3}
                  placeholder="Describe your goal in a sentence..."
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{goal.length}/150</p>
              </div>
            )}

            {error && (
              <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-6">
              <button
                onClick={handleBack}
                disabled={step === 1}
                className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              {step < 4 ? (
                <button
                  onClick={handleNext}
                  disabled={!canAdvance()}
                  className="flex items-center gap-1 px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> Get My Habits
                    </>
                  )}
                </button>
              )}
            </div>
          </>
        )}

        {/* Results */}
        {step === 5 && (
          <>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Your habit suggestions
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Click any habit to customise and add it.
                </p>
              </div>
              <button
                onClick={handleReset}
                className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <RotateCcw className="w-3 h-3" /> Start over
              </button>
            </div>

            <div className="flex flex-col gap-3 mt-4">
              {suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => onSuggestionSelect(suggestion, suggestions)}
                  className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-sm transition-all text-left group"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                    style={{ backgroundColor: suggestion.colorCode }}
                  >
                    {suggestion.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {suggestion.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {suggestion.description}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 flex-shrink-0 transition-colors" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PageHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-3">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-indigo-500" />
        <span className="font-semibold text-gray-900 dark:text-white text-sm">Discover Habits</span>
      </div>
    </div>
  );
}
